/**
 * Un pedido de una pantalla de Emails con su estado: cargando, error y el dato. Como en Estadísticas de WhatsApp:
 * - al cambiar la `clave` (otro rango, otra campaña, otro filtro) lo anterior se borra: un número de otra consulta bajo
 *   el título nuevo miente;
 * - al cambiar `refresco` (terminó una actualización de Snov) se vuelve a pedir lo MISMO sin borrar: la pantalla no
 *   vuelve al esqueleto cada media hora;
 * - una respuesta vieja que llega tarde se descarta (número de secuencia);
 * - un refresco que falla deja lo último que se vio y dice que no se pudo actualizar, salvo un 404: eso ya no es de
 *   este comercio (el equipo deshizo una asignación) y no se sigue mostrando, con sus mails y teléfonos;
 * - el error de la consulta anterior no queda a la vista mientras carga la nueva.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { leerError, leerRespuesta, textoError } from "../../wa-oficial/logic/respuesta";

export interface Pedido<D> {
  dato: D | null;
  cargando: boolean;
  error: string;
  code: string;
  recargar: () => void;
}

export function usePedido<D>(pedir: () => Promise<unknown>, clave: string, { activo = true, refresco = 0 }: { activo?: boolean; refresco?: number } = {}): Pedido<D> {
  const [dato, setDato] = useState<D | null>(null);
  const [cargando, setCargando] = useState(activo);
  const [error, setError] = useState("");
  const [code, setCode] = useState("");
  const seq = useRef(0);
  const claveVista = useRef(clave);
  const pedirRef = useRef(pedir);
  pedirRef.current = pedir;

  const correr = useCallback(async () => {
    const mio = ++seq.current;
    setCargando(true);
    let nuevo: D | null = null;
    let err = "";
    let cod = "";
    try {
      const l = leerRespuesta<D>(await pedirRef.current());
      if (!l.ok) {
        err = textoError(l);
        cod = l.code;
      } else if (l.data === undefined || l.data === null) err = "El servidor no devolvió datos.";
      else nuevo = l.data;
    } catch (e) {
      const x = leerError(e);
      err = textoError(x);
      cod = x.code;
    }
    if (mio !== seq.current) return;
    if (nuevo !== null) setDato(nuevo);
    else if (cod === "NOT_FOUND") setDato(null);
    setError(err);
    setCode(cod);
    setCargando(false);
  }, []);

  useEffect(() => {
    if (!activo) return;
    if (claveVista.current !== clave) {
      claveVista.current = clave;
      setDato(null);
      setError("");
      setCode("");
    }
    void correr();
  }, [clave, correr, activo, refresco]);

  return { dato, cargando, error, code, recargar: () => void correr() };
}
